/**
 * Ce qu'on achemine soi-même : les pastilles « par nos soins », matériaux et camions.
 *
 * *Demande de Chris, 19/08/2026 : « j'ai besoin d'un bouton pour filtrer les articles pour
 * lesquels livraison par nos soins est précisé », précisé aussitôt : « il faut faire
 * apparaître la ligne avec l'article et la ligne avec les heures le jour ».*
 *
 * ## Ce que la version précédente devait contourner, et pourquoi elle n'a plus à le faire
 *
 * Jusqu'à la migration 039, `mode_livraison` n'existait **que** sur la pastille de
 * transport : une pastille de livraison ne portait que son heure limite, et rien qui dise
 * qui apporte le matériau. Le filtre ne pouvait donc pas partir de l'article. Il partait de
 * la rotation de camion et **remontait à la tâche** — la seule jointure disponible entre le
 * moyen et son chargement — puis gardait toutes les lignes d'article de cette tâche, qu'elles
 * aient un rapport avec ce camion ou non.
 *
 * C'était large, et c'était assumé faute de mieux. **La 039 a posé les six champs
 * d'acheminement sur la pastille de livraison**, donc la question se pose maintenant là où
 * elle se décide : *cette* pastille, ce volume-là, est-il acheminé par nous ?
 *
 * ## Ce que le filtre garde
 *
 * **Toute ligne qui répond « par nos soins »**, aux trois endroits où la question se pose :
 *
 *   - la **ligne de contre-étude** elle-même (041) — ce que le chiffrage a décidé. Ajoutée le
 *     25/08/2026, et c'est la moitié qui manquait ;
 *   - une **livraison** en interne — le matériau qu'on va chercher soi-même. C'est la
 *     demande d'origine, et elle attrape les articles ;
 *   - une **rotation** en interne — le camion ou le chauffeur qui l'assure. C'est le moyen à
 *     mobiliser, et c'est la seconde ligne que Chris a demandé de voir.
 *
 * ## Pourquoi la ligne, et pas seulement ses pastilles
 *
 * *Chris, 25/08/2026 : « j'ai cliqué sur le filtre livré par nos soins dans la vue planning et
 * cela n'a pas l'air de fonctionner ; il faudrait faire apparaître que les lignes sur
 * lesquelles on a paramétré livré par nos soins DANS LA CONTRE-ÉTUDE ».*
 *
 * Partir des seules pastilles laissait un trou que la 041 n'a comblé qu'à moitié : elle fait
 * descendre l'acheminement de la ligne sur les pastilles **existantes**, mais une ligne dont
 * aucune pastille n'a encore été posée n'a rien où le poser. Elle restait donc invisible —
 * alors que c'est exactement celle qu'on cherche : un matériau qu'on doit aller chercher et
 * dont personne n'a encore placé le moindre volume au calendrier. Le filtre servait à
 * organiser des tournées à partir d'une vue vide.
 *
 * La ligne ne remplace pas la pastille, elle s'y **ajoute** : la 041 dit que la pastille fait
 * foi pour ce qu'elle porte, et ça ne bouge pas. Ici on ne décide de rien, on choisit ce qu'on
 * affiche — et une ligne annoncée « par nos soins » au chiffrage doit être sous les yeux du
 * responsable transport, quoi qu'en disent ses pastilles.
 *
 * Une tâche entre dans la vue dès qu'une de ses lignes y entre. **Rien n'est gardé « par
 * voisinage »** : une ligne de la même tâche qui ne déclare aucun acheminement interne ne
 * s'affiche pas. Le prix de cette précision est qu'un camion dont personne n'a renseigné le
 * mode n'apparaît pas à côté de son chargement — d'où le compteur ci-dessous, qui ne compte
 * pas seulement les rotations mais les **deux** natures de pastilles.
 */
import { formaterKm } from "@planif/moteur";
import type { DepoVue, NoeudVue, OuvrageVue, RessourceVue } from "@planif/moteur";

/** Ce que « par nos soins » vaut en base : `mode_livraison = 'interne'` (020, puis 039). */
export const PAR_NOS_SOINS = "interne";

/**
 * Cette ligne apporte-t-elle quelque chose par nos soins ?
 *
 * Les trois porteurs sont lus par la même fonction, et c'est délibéré : le responsable
 * transport leur pose la même question — « est-ce moi qui m'en occupe ? ». Trois prédicats
 * jumeaux finiraient par différer d'une condition.
 *
 * **La ligne d'abord**, parce que c'est la seule réponse qui existe toujours : une ligne se
 * chiffre avant qu'aucune pastille ne soit posée.
 */
export function porteDuParNosSoins(r: RessourceVue): boolean {
  return (
    r.modeLivraison === PAR_NOS_SOINS ||
    r.planning.livraisons.some((l) => l.modeLivraison === PAR_NOS_SOINS) ||
    r.planning.transports.some((t) => t.modeLivraison === PAR_NOS_SOINS)
  );
}

/** Cette tâche est-elle concernée ? Dès qu'une de ses lignes l'est. */
export function tacheAcheminee(tache: OuvrageVue): boolean {
  return tache.ressources.some(porteDuParNosSoins);
}

/**
 * Les lignes à montrer dans une tâche retenue.
 *
 * Exactement celles qui déclarent un acheminement interne — ni plus, ni moins. La
 * main-d'œuvre de pose, une pelle, un matériau livré par le fournisseur : rien de tout cela
 * ne dit quoi que ce soit du transport, et les garder rendrait la vue aussi chargée que le
 * planning complet.
 */
export function lignesAcheminees(tache: OuvrageVue): readonly RessourceVue[] {
  if (!tacheAcheminee(tache)) return [];
  return tache.ressources.filter(porteDuParNosSoins);
}

/** Les tâches d'un arbre, à plat. Une tâche est une feuille : seul un chapitre a des enfants. */
function tachesDe(noeuds: readonly NoeudVue[]): OuvrageVue[] {
  return noeuds.flatMap((n) => (n.nature === "ouvrage" ? [n] : tachesDe(n.enfants)));
}

/**
 * Combien de pastilles n'ont pas encore dit QUI les assure.
 *
 * Le pendant exact du compteur de codes non tranchés du filtre porte-engin, et pour la même
 * raison : `mode_livraison` a **trois** états, et une pastille dont personne n'a renseigné le
 * mode disparaît de cette vue sans rien dire. Le responsable transport lirait une liste
 * courte et organiserait ses camions en croyant avoir tout — c'est le pire mode de panne d'un
 * écran d'alerte, et la leçon des absences non rattachées du 16/08.
 *
 * **Les deux natures y entrent depuis la 039**, et c'est ce qui rend le resserrement du
 * filtre sans danger : la version précédente gardait les articles d'une tâche par voisinage,
 * ce qui masquait qu'aucun d'eux n'avait déclaré sa provenance. Maintenant ils sortent de la
 * vue — et le compteur les nomme.
 *
 * Compté sur l'arbre ENTIER et non sur ce que le filtre laisse voir : c'est justement ce qui
 * manque à l'écran qu'il faut annoncer. Les tâches écartées du chiffrage n'y entrent pas —
 * leurs camions ne rouleront pas et leurs matériaux ne seront pas commandés.
 */
export function pastillesSansMode(depo: DepoVue): {
  readonly livraisons: number;
  readonly rotations: number;
  readonly total: number;
} {
  let livraisons = 0;
  let rotations = 0;
  for (const tache of tachesDe(depo.racines)) {
    if (!tache.retenu) continue;
    for (const r of tache.ressources) {
      livraisons += r.planning.livraisons.filter((l) => l.modeLivraison === null).length;
      rotations += r.planning.transports.filter((t) => t.modeLivraison === null).length;
    }
  }
  return { livraisons, rotations, total: livraisons + rotations };
}

/* ---------------------------------------------------------------------------
 * L'acheminement d'un besoin daté, pour le tableau de bord transport
 * ------------------------------------------------------------------------- */

/**
 * Ce besoin daté est-il acheminé par nos soins ?
 *
 * *Demande de Chris, 03/09/2026 : le tableau de bord transport affiche désormais **toutes** les
 * livraisons, « en distinguant celles par nos soins » — savoir qu'un fournisseur livre 20 t à
 * 8 h change l'organisation du chantier, même si aucun camion du groupe ne bouge.*
 *
 * Le besoin daté porte déjà la réponse descendue de la ligne et de sa pastille (039, 041) : il
 * n'y a rien à recalculer, et surtout rien à redemander à l'arbre de contre-étude. La même
 * valeur qu'ailleurs — `interne` — est lue par la même constante, sans quoi deux endroits
 * finiraient par ne pas s'accorder sur ce que « par nos soins » veut dire.
 */
export function estParNosSoins(besoin: { readonly modeLivraison: string | null }): boolean {
  return besoin.modeLivraison === PAR_NOS_SOINS;
}

/** Le texte d'un champ facultatif, ou `null` quand il n'y a rien à dire. */
function renseigne(valeur: string | null): string | null {
  return valeur !== null && valeur !== "" ? valeur : null;
}

/**
 * D'où vient ce qu'on transporte, où ça va et combien ça fait — en une phrase, ou rien du tout.
 *
 * *Demande de Chris, 03/09/2026 : « rajouter les pastilles des tonnages ou des articles pour
 * compléter l'information avec également toutes les infos des heures, provenances etc. pour bien
 * aiguiller le responsable ».*
 *
 * L'ordre suit la question du responsable transport : **qui l'apporte**, puis **le trajet**. Un
 * matériau du groupe nomme sa carrière ; un matériau externe nomme son fournisseur.
 *
 * ## Le trajet entier depuis le 09/09/2026
 *
 * La phrase s'arrêtait au point de départ : « carrière Bougue · Bougue ». Le responsable devait
 * rouvrir la contre-étude du chantier pour savoir **où ça va** et **combien de km** — les deux
 * valeurs qui décident du camion. Elles arrivent désormais avec le besoin, prises ensemble au
 * serveur (`trajetDuBesoin`) : le km est la distance entre ces deux villes-là, et non une
 * mesure rapportée d'ailleurs.
 *
 * La flèche porte le sens même quand le départ manque — « → Castets » se lit comme une
 * destination, là où la ville seule se confondrait avec un chargement. Et la ville de chargement
 * peut être **hors référentiel** depuis la 073 : la lire seulement dans `villeProvenance`
 * laissait une ligne « par nos soins » hors groupe sans aucun départ affiché.
 *
 * **Rien n'est inventé quand rien n'est renseigné** : une chaîne vide, que l'écran affiche comme
 * une case vide. Écrire « fournisseur inconnu » ferait lire une réponse là où il n'y a qu'un
 * champ à remplir — et c'est `pastillesSansMode` qui compte ce qui manque, à un seul endroit.
 * Un km absent n'écrit donc rien non plus ; c'est `motifSansKm` qui dit pourquoi, en infobulle.
 */
export function direLaProvenance(
  besoin: {
    readonly duGroupe: boolean;
    readonly carriereCode: string | null;
    readonly modeLivraison: string | null;
    readonly fournisseur: string | null;
    readonly villeProvenance: string | null;
    readonly villeProvenanceLibre: string | null;
    readonly villeLivraison: string | null;
    readonly km: string | null;
    readonly kmEstForce: boolean;
  },
  nommerLaCarriere: (code: string) => string | null,
): string {
  const morceaux: string[] = [];

  if (besoin.modeLivraison === PAR_NOS_SOINS) morceaux.push("par nos soins");
  else if (besoin.modeLivraison === "transporteur_externe") morceaux.push("transporteur");
  else if (besoin.modeLivraison === "fournisseur") morceaux.push("livré fournisseur");

  if (besoin.duGroupe) {
    const carriere =
      besoin.carriereCode === null ? null : nommerLaCarriere(besoin.carriereCode);
    // « Du groupe » sans la carrière reste une information : le matériau ne s'achète pas
    // dehors, même si personne n'a encore dit de quel site il sort.
    morceaux.push(carriere === null ? "du groupe" : `carrière ${carriere}`);
  } else if (renseigne(besoin.fournisseur) !== null) {
    morceaux.push(besoin.fournisseur as string);
  }

  // Les deux villes sont exclusives en base (073) : jamais un nom saisi à côté d'un nom choisi.
  const depart = renseigne(besoin.villeProvenance) ?? renseigne(besoin.villeProvenanceLibre);
  const arrivee = renseigne(besoin.villeLivraison);
  if (arrivee !== null) morceaux.push(depart === null ? `→ ${arrivee}` : `${depart} → ${arrivee}`);
  else if (depart !== null) morceaux.push(depart);

  if (besoin.km !== null) {
    // Même formulation que le champ de la contre-étude : une seule façon d'écrire un km.
    morceaux.push(`${formaterKm(besoin.km)} km${besoin.kmEstForce ? " (saisi)" : ""}`);
  }

  return morceaux.join(" · ");
}
